import type { Lang } from "./i18n";

export const LOCALES: Record<Lang, string> = {
  zh: "zh-CN", en: "en-IE", nl: "nl-NL", fr: "fr-FR", de: "de-DE", it: "it-IT", es: "es-ES", pt: "pt-PT", cs: "cs-CZ", pl: "pl-PL", hu: "hu-HU",
};

export function money(cents: number, lang: Lang, opts: { sign?: boolean } = {}) {
  const s = new Intl.NumberFormat(LOCALES[lang], { style: "currency", currency: "EUR" }).format(Math.abs(cents) / 100);
  if (cents < 0) return `−${s}`;
  return opts.sign && cents > 0 ? `+${s}` : s;
}

/** Parse "12,50" / "12.5" / "€ 1.234,56" into cents. Returns null when invalid. */
export function parseMoney(input: string): number | null {
  let s = input.replace(/[€\s]/g, "");
  if (!s) return null;
  // "1.234,56" → "1234.56"; "1,234.56" → "1234.56"
  if (/^\d{1,3}(\.\d{3})+(,\d{1,2})?$/.test(s)) s = s.replace(/\./g, "").replace(",", ".");
  else if (/^\d{1,3}(,\d{3})+(\.\d{1,2})?$/.test(s)) s = s.replace(/,/g, "");
  else s = s.replace(",", ".");
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  const cents = Math.round(Number(s) * 100);
  return Number.isFinite(cents) ? cents : null;
}

export const centsToInput = (cents: number | null | undefined) => (cents == null ? "" : (cents / 100).toFixed(2));

export function todayIso() {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
}
export const thisMonth = () => todayIso().slice(0, 7);

export function shiftMonth(month: string, delta: number) {
  const [y, m] = month.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return d.toISOString().slice(0, 7);
}

export function formatDate(iso: string, lang: Lang, opts: Intl.DateTimeFormatOptions = { day: "numeric", month: "short" }) {
  const d = iso.length === 10 ? new Date(iso + "T12:00:00") : new Date(iso);
  return new Intl.DateTimeFormat(LOCALES[lang], opts).format(d);
}

export function formatMonth(month: string, lang: Lang) {
  return new Intl.DateTimeFormat(LOCALES[lang], { month: "long", year: "numeric" }).format(new Date(month + "-15T12:00:00"));
}

export function formatNumber(n: number, lang: Lang, maxFrac = 1) {
  return new Intl.NumberFormat(LOCALES[lang], { maximumFractionDigits: maxFrac }).format(n);
}

export function relativeTime(iso: string, lang: Lang) {
  const diff = (new Date(iso).getTime() - Date.now()) / 1000;
  const rtf = new Intl.RelativeTimeFormat(LOCALES[lang], { numeric: "auto" });
  const abs = Math.abs(diff);
  if (abs < 60) return rtf.format(Math.round(diff), "second");
  if (abs < 3600) return rtf.format(Math.round(diff / 60), "minute");
  if (abs < 86400) return rtf.format(Math.round(diff / 3600), "hour");
  return rtf.format(Math.round(diff / 86400), "day");
}

export const initials = (s: string) => s.trim().replace(/\s+/g, " ").split(" ").map((w) => w[0]).join("").slice(0, 2) || "?";
